import {USER_KEYS} from "@/api/userApi";
import type {UserDetailSerializer} from "@/generated/DjangoTypes";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedCompany} from "@/hooks/useUserHooks";
import {useCreateAgencyStore} from "@/store/createAgency";
import {
  hasClosedPaymentFailedModalAtom,
  isConfirmEmailModalOpenAtom,
  isFinishRegistrationModalVisibleAtom,
  isWelcomeToMultiAccountModalOpenAtom,
  paymentFailedModalDataAtom,
  selectedCompanyIdAtom,
} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import useUserStore from "@/store/user";
import {useLocation} from "@tanstack/react-router";
import {useAtomValue, useSetAtom} from "jotai";
import {useEffect} from "react";

const useFetchUser = () => {
  const location = useLocation();
  const userId = useTokenStore(state => state.userId);
  const user = useUserStore(state => state.user);
  const selectedCompany = useSelectedCompany();
  const setSelectedCompanyId = useSetAtom(selectedCompanyIdAtom);
  const setUserData = useUserStore(state => state.setUserData);
  const hasClosedPaymentFailedModal = useAtomValue(hasClosedPaymentFailedModalAtom);
  const agencySetter = useCreateAgencyStore(state => state.setter);
  const setPaymentFailedModalData = useSetAtom(paymentFailedModalDataAtom);
  const setIsConfirmEmailModalOpen = useSetAtom(isConfirmEmailModalOpenAtom);
  const setIsFinishRegistrationModalVisible = useSetAtom(isFinishRegistrationModalVisibleAtom);
  const setIsWelcomeToMultiAccountModalVisible = useSetAtom(isWelcomeToMultiAccountModalOpenAtom);

  const {refetch, data, error} = useGenericQuery<UserDetailSerializer>({
    queryKey: USER_KEYS.qUser(userId!),
    enabled: !!userId,
  });

  useEffect(() => {
    if (data) {
      setUserData(data);

      if (data.user_access_level === "INFLUENCER_ADMIN" || data.user_access_level === "COMPANY_ADMIN") {
        agencySetter(state => {
          state.form.sales_email = data.email!;
        });
      }

      const isAgency = data.user_access_level === "AGENCY" || data.agencies?.[0]?.agency.multiple_brands;
      const isBrand = !isAgency && data.user_access_level === "SINGLE_BRAND";

      if (!selectedCompany && !isAgency && data.user_access_level !== "INFLUENCER_ADMIN") {
        setSelectedCompanyId(data.sub_brands?.[0]?.company.id!);
      }

      // TODO: fix flickering
      if (!location.pathname.includes("verify-account") && !import.meta.env.VITE_IS_E2E) {
        setIsConfirmEmailModalOpen(!!data.requires_email_confirmation && data.user_access_level !== "SUB_BRAND");
      }

      const isMissingRegistrationData = !selectedCompany?.interests?.length || !selectedCompany?.company_name;
      // On /onboarding the AI flow is itself the "finish registration" path - don't stack the modal on top of it.
      setIsFinishRegistrationModalVisible(
        isBrand &&
          !data.created_by_ai_onboarding &&
          isMissingRegistrationData &&
          !location.pathname.includes("/onboarding"),
      );
      setIsWelcomeToMultiAccountModalVisible(!!isAgency && !data.agencies?.[0]?.agency?.filled_out_agency_data);
    }
  }, [data, location.pathname, setUserData, setIsFinishRegistrationModalVisible, selectedCompany]);

  useEffect(() => {
    const failedInvoices = user.payment_failed_invoices;

    if (failedInvoices?.length && !hasClosedPaymentFailedModal) {
      setPaymentFailedModalData(failedInvoices);
    }
  }, [user.payment_failed_invoices, hasClosedPaymentFailedModal]);

  return {
    refetch,
    userId,
    error,
  };
};

export {useFetchUser};
